import { ComponentProps, forwardRef } from 'react';

import { AvatarSkeleton, TextSkeleton } from '@/components/Skeleton';

import { fontStyles } from '@/styles/typography';

import { Avatar } from '@/ui/Avatars/Avatar';
import { Checkbox } from '@/ui/Checkbox';
import { CheckmarkIcon } from '@/ui/Icons/CheckmarkIcon';

import { classNames } from '@/utils/classnames';

import { MultiselectItem } from '..';

type MultiselectOptionProps = ComponentProps<'div'> & {
  item: MultiselectItem;
  selected: boolean;
  isLoading?: boolean;
  multiselect?: boolean;
};

export const MultiselectOption = forwardRef<HTMLDivElement, MultiselectOptionProps>(
  ({ item, selected, className, isLoading, onClick, multiselect, ...otherProps }, ref) => {
    const checked = selected ? 'checked' : 'unchecked';

    return (
      <div
        role="menuitem"
        ref={ref}
        className={classNames(
          'h-10 px-3 flex shrink-0 flex-row items-center rounded-md outline-transparent border-0 outline-none',
          { 'cursor-pointer hover:bg-token-gray-200 focus-visible:bg-token-gray-200': !isLoading },
          { 'pr-10': !multiselect && !selected },
          className,
        )}
        onClick={isLoading ? undefined : onClick}
        {...otherProps}
      >
        {multiselect && <Checkbox checked={checked} disabled={isLoading} />}
        {isLoading && (
          <div
            className={classNames('flex flex-row gap-1.5 items-center', { 'pl-2.5': multiselect })}
          >
            <AvatarSkeleton size={16} />
            <TextSkeleton height={14} width={88} />
          </div>
        )}
        {!isLoading && (
          <div className="flex items-center justify-between w-full gap-3">
            <label
              className={fontStyles({
                variant: 'BODY_MD',
                class: [
                  'cursor-pointer flex flex-row gap-1.5 items-center',
                  multiselect ? 'pl-3' : undefined,
                ],
              })}
            >
              {item.icon && (
                <Avatar
                  src={item.icon}
                  fallbackSrc="/images/chains/none.svg"
                  alt={item.iconAlt}
                  size={16}
                  radius={item.iconRadius}
                />
              )}
              <span>{item.label}</span>
            </label>
            {!multiselect && selected && <CheckmarkIcon />}
          </div>
        )}
      </div>
    );
  },
);

MultiselectOption.displayName = 'MultiselectOption';
